import React from "react";
import { TypeAnimation } from "react-type-animation";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import { motion } from "framer-motion";
import ProfileImg from "../assets/profile.jpg";

const Banner = ({ scrollToContact }) => {
  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden pt-32 md:pt-28 lg:pt-24 pb-16"
      id="home"
    >
      {/* Decorative elements - elegant and subtle */}
      <div className="absolute top-20 right-10 w-32 h-32 bg-gradient-to-br from-gold-light to-gold opacity-20 rounded-full blur-2xl hidden lg:block" />
      <div className="absolute bottom-40 left-10 w-24 h-24 bg-gradient-to-br from-navy-light to-navy opacity-15 rounded-full blur-xl hidden lg:block" />
      <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-gradient-to-br from-sage to-sage-dark opacity-20 rounded-full blur-lg hidden lg:block" />

      <div className="container mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-center gap-12 lg:gap-20">
          {/* Text content */}
          <div className="flex-1 space-y-6 md:space-y-8">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-block"
            >
              <span className="inline-block px-5 py-2.5 md:px-7 md:py-3 bg-sand-light border border-sand-dark/30 font-mono font-medium text-xs md:text-sm tracking-wide shadow-elegant">
                👋 Bienvenue sur mon portfolio
              </span>
            </motion.div>

            {/* Main heading */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h1 className="h1">
                ANTHONY
                <br />
                <span className="relative inline-block">
                  REY
                  <span className="absolute -bottom-2 left-0 w-full h-3 bg-gradient-to-r from-gold to-gold-rose opacity-60 -z-10 blur-sm" />
                </span>
              </h1>
            </motion.div>

            {/* Typing animation */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-2xl md:text-3xl lg:text-4xl font-primary font-semibold"
            >
              <span className="text-charcoal">Je suis un </span>
              <TypeAnimation
                sequence={[
                  "Développeur Web",
                  2000,
                  "Commercial",
                  2000,
                  "Passionné",
                  2000,
                ]}
                speed={50}
                className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-gold-rose to-terracotta"
                wrapper="span"
                repeat={Infinity}
              />
            </motion.div>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg lg:text-xl max-w-xl text-charcoal/80"
            >
              Développeur fullstack passionné, je crée des expériences web
              modernes et performantes en combinant créativité et expertise
              technique.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 items-start sm:items-center"
            >
              <button
                className="btn-primary"
                onClick={scrollToContact}
              >
                Contactez-moi
              </button>

              <a
                href="https://drive.google.com/file/d/1JUYoQiLBHeAZ--5Ht6sKvl03vDrg5-ee/view?usp=sharing"
                target="_blank"
                className="btn-outline"
                rel="noreferrer"
              >
                Télécharger mon CV
              </a>
            </motion.div>

            {/* Social links */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex gap-4"
            >
              <a
                href="https://www.linkedin.com/in/anthony-rey-16108695/"
                target="_blank"
                rel="noreferrer"
                aria-label="Profil LinkedIn d'Anthony Rey"
                className="w-14 h-14 flex items-center justify-center bg-white border border-stone/40 shadow-elegant hover:shadow-elegant-hover hover:-translate-y-1 hover:border-navy/50 transition-all"
              >
                <FaLinkedin className="text-2xl text-navy" />
              </a>
              <a
                href="https://github.com/tizen21"
                target="_blank"
                rel="noreferrer"
                aria-label="Profil GitHub d'Anthony Rey"
                className="w-14 h-14 flex items-center justify-center bg-white border border-stone/40 shadow-elegant hover:shadow-elegant-hover hover:-translate-y-1 hover:border-charcoal/50 transition-all"
              >
                <FaGithub className="text-2xl text-charcoal" />
              </a>
            </motion.div>
          </div>

          {/* Image/Visual element */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex-1 relative hidden lg:flex justify-center items-center"
          >
            <div className="relative">
              {/* Main card with image */}
              <div className="relative w-[380px] h-[480px] border border-stone/30 bg-gradient-to-br from-sand-light via-pearl to-stone shadow-elegant-xl overflow-hidden group">
                <div className="absolute inset-0">
                  <img
                    src={ProfileImg}
                    alt="Anthony Rey"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                  {/* Subtle overlay for better integration */}
                  <div className="absolute inset-0 bg-gradient-to-t from-sand/20 to-transparent" />
                </div>
              </div>
              {/* Decorative accent - elegant shapes */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-gold to-gold-rose opacity-30 rounded-full blur-lg" />
              <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-navy to-navy-dark opacity-30 rounded-full blur-md" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Banner;
